import { DocumentTemplate } from '../types/document-template';
import { Field, StructureType } from '../types/fields';
import { AnchorTreeNode } from '../types/anchor-tree';
import { findLeaf, getExpectedFieldIdsForLeaf } from './anchor-tree.utils';

/**
 * compare the fields in a template structure to the structure and return true if they are out of sync
 * @param template
 * @param structure
 */
export function isTemplateOutOfSync(template: Pick<DocumentTemplate, 'structure' | 'fieldsStructureId'>, structure?: StructureType): boolean {
  if (!structure || template.fieldsStructureId !== structure.id) {
    return true;
  }
  const templateFields = Object.keys(template.structure);
  const structureFields = structure.fieldIds;
  if (templateFields.length !== structureFields.length) {
    return true;
  }

  for (const fieldId of templateFields) {
    if (!structureFields.includes(fieldId)) {
      return true;
    }
  }
  for (const fieldId of structureFields) {
    if (!templateFields.includes(fieldId)) {
      return true;
    }
  }

  return false;
}

export type TemplateAnchorStatus = 'ok' | 'anchor-mismatch' | 'unplaced';

/** The two-sided difference between a template's anchors and its leaf's expected set. */
export interface TemplateAnchorDiff {
  /** Expected anchor names with no stored value on the template. */
  missing: string[];
  /** Anchor names stored on the template that the leaf does not expect. */
  extra: string[];
}

/** A value counts as "missing" when empty; `false` is valid for boolean fields. */
function isAnchorValueMissing(value: unknown, type: Field['type']): boolean {
  if (type === 'boolean') {
    return value === undefined || value === null;
  }
  return value === undefined || value === null || value === '';
}

/**
 * Diffs a template's stored anchors against the expected set of its leaf
 * (path decisive anchors ∪ leaf anchor fields), in BOTH directions.
 *
 * Notes:
 * - Anchor keys produced during extraction are keyed by field NAME (matching
 *   `OcrResult`), so lookups resolve field ids → names via `fieldsById`.
 * - Expected field ids that don't resolve (deleted fields) contribute no
 *   `missing` entry — their name is unknowable, so any value still stored for
 *   them surfaces as `extra` instead.
 * - Every stored key outside the expected set is `extra`, regardless of its
 *   value: assignment and re-extraction replace `anchors` wholesale with a
 *   leaf-scoped extraction, so leftovers are always drift.
 * - An unplaced template has no expected set; the diff is empty.
 */
export function getTemplateAnchorDiff(
  template: Pick<DocumentTemplate, 'anchors' | 'leafNodeId'>,
  treeRoot: AnchorTreeNode | null,
  fieldsById: Map<string, Pick<Field, 'id' | 'name' | 'type'>>
): TemplateAnchorDiff {
  if (!template.leafNodeId) {
    return { missing: [], extra: [] };
  }
  const anchors = template.anchors ?? {};
  const expectedNames = new Set<string>();
  const missing: string[] = [];

  for (const fieldId of getExpectedFieldIdsForLeaf(treeRoot, template.leafNodeId)) {
    const field = fieldsById.get(fieldId);
    if (!field) {
      continue;
    }
    expectedNames.add(field.name);
    if (isAnchorValueMissing(anchors[field.name], field.type)) {
      missing.push(field.name);
    }
  }

  return { missing, extra: Object.keys(anchors).filter(name => !expectedNames.has(name)) };
}

/**
 * Computes the anchor status of a template against the global anchor tree.
 *
 * - `'unplaced'` — the template is not assigned to any (existing) tree leaf,
 *   or its id is not listed in that leaf's `templateIds`. Unplaced templates
 *   are unreachable for matching.
 * - `'anchor-mismatch'` — the template is placed, but its anchors and the
 *   leaf's expected set are not aligned: an expected anchor has no stored
 *   value, or the template stores an anchor the leaf does not expect. This is
 *   the derived "stale" state: it covers leaf-set edits, moves between leaves,
 *   and template file replacement.
 * - `'ok'` — placed, and the stored anchors match the expected set exactly.
 *
 * See {@link getTemplateAnchorDiff} for the alignment rules.
 */
export function getTemplateAnchorStatus(
  template: Pick<DocumentTemplate, 'id' | 'anchors' | 'leafNodeId'>,
  treeRoot: AnchorTreeNode | null,
  fieldsById: Map<string, Pick<Field, 'id' | 'name' | 'type'>>
): TemplateAnchorStatus {
  if (!template.leafNodeId) {
    return 'unplaced';
  }
  const leaf = findLeaf(treeRoot, template.leafNodeId);
  if (!leaf || !(leaf.templateIds ?? []).includes(template.id)) {
    return 'unplaced';
  }

  const { missing, extra } = getTemplateAnchorDiff(template, treeRoot, fieldsById);
  return missing.length > 0 || extra.length > 0 ? 'anchor-mismatch' : 'ok';
}

/**
 * Names of the expected anchors (path ∪ leaf) that are missing a value on the
 * template. Empty when the template is unplaced or complete.
 */
export function getMissingAnchorNames(
  template: Pick<DocumentTemplate, 'anchors' | 'leafNodeId'>,
  treeRoot: AnchorTreeNode | null,
  fieldsById: Map<string, Pick<Field, 'id' | 'name' | 'type'>>
): string[] {
  return getTemplateAnchorDiff(template, treeRoot, fieldsById).missing;
}

/**
 * Names of the anchors stored on the template that its leaf does not expect —
 * leftovers from a previous leaf or an earlier leaf anchor set. Empty when the
 * template is unplaced or aligned.
 */
export function getExtraAnchorNames(
  template: Pick<DocumentTemplate, 'anchors' | 'leafNodeId'>,
  treeRoot: AnchorTreeNode | null,
  fieldsById: Map<string, Pick<Field, 'id' | 'name' | 'type'>>
): string[] {
  return getTemplateAnchorDiff(template, treeRoot, fieldsById).extra;
}

/**
 * @deprecated Use {@link getTemplateAnchorStatus} — the check is now scoped to
 * the template's leaf placement instead of all global anchor fields. Returns
 * true only for the `'anchor-mismatch'` status (unplaced templates surface a
 * separate flag).
 */
export function isTemplateAnchorMismatch(
  template: Pick<DocumentTemplate, 'id' | 'anchors' | 'leafNodeId'>,
  treeRoot: AnchorTreeNode | null,
  fieldsById: Map<string, Pick<Field, 'id' | 'name' | 'type'>>
): boolean {
  return getTemplateAnchorStatus(template, treeRoot, fieldsById) === 'anchor-mismatch';
}
